/**
 * `pageBreakModel` — pure, DOM-free block-packing paginator (RECUR-27304
 * Delta v2). Given the already-measured geometry of an editor's top-level
 * blocks, plus the sticky groups `buildPaginationGroups` built from the live
 * DOM, decides which group starts each NEW page — replicating headless
 * Chromium's native paged-media fragmentation (the SERVER exporter,
 * `exportToPdfServer.ts`, always the production "Export .pdf" path — see
 * plan D2) closely enough that the in-editor seam lands at the block that
 * actually starts each page, instead of a fixed `k×pageHeight` interval.
 *
 * Mirrors ONLY `planPages`' page-start decision — PUSH (group doesn't fit the
 * remaining space, but fits a fresh page) and EXTEND (group taller than a
 * whole page — kept together, no premature seam inside it) — reproduced here
 * without any of `planPages`' DOM-mutating margin pushes, content-sized final
 * page, or raster-only bottom-padding backstop (those exist to make the
 * html2canvas SLICE look right; Chromium's native fragmentation has no
 * equivalent, and `getServerPageHeightPx` already reflects the SERVER page's
 * usable content height, so no separate padding constant is needed here).
 * exportToPdf.ts:602-917 is the pattern reference, not a dependency — no DOM
 * access, no image decode, every block is already laid out by the live
 * editor.
 *
 * Delta v3: returns the flat-unit INDEX of each page-start (was its
 * `offsetTop`) — ONE model output that `pageStartUnits.ts` now projects two
 * ways: the overlay maps an index to `geometry[index].offsetTop` (byte-
 * identical Ys to before), and the server exporter maps it to the LIVE
 * element that starts the page, so it can force Chromium to break exactly
 * there (see exportToPdfServer.ts's injection).
 *
 * Delta v4 (gap-display, RECUR-27304): the PDF/export behaviour above is
 * UNCHANGED — every page-start decision is identical. `pageBreakModelWithGaps`
 * additionally reports the unused space each PUSH/forced break leaves behind
 * on the page above it, so the editor can render that same blank space
 * instead of just a thin seam line. `pageBreakModel` (this export) is now a
 * thin wrapper over it. See `pageStartUnits.ts` for how the gap is projected
 * onto LIVE (post-gap) coordinates for the overlay while the model itself
 * keeps reasoning in gap-free (normalized) coordinates — the two spaces are
 * documented at each boundary in that file to avoid mixing them up.
 */

/** A single top-level editor block's measured geometry (CSS px). */
export interface BlockGeometry {
    offsetTop: number;
    offsetHeight: number;
}

/**
 * A sticky-heading-or-standalone group of block indices, inclusive of both
 * ends. Structurally identical to `exportToPdf.ts`'s module-private
 * `PaginationGroup` (not exported) — defined locally so this module stays
 * DOM-free; `buildPaginationGroups`'s return value satisfies this shape.
 */
export interface PaginationGroup {
    startIdx: number;
    endIdx: number;
}

/**
 * One page-start decision from the fit-loop: the flat-unit INDEX that PUSHes
 * (or is forced onto) a new page, paired with the vertical space (CSS px)
 * left unused on the page it broke away from (RECUR-27304 gap-display —
 * "make the editor show the same blank space the exported PDF leaves
 * behind"). `unusedPx` is computed at the moment of the break, before
 * `pageTop`/`currentPageContentEnd` roll over to the new page, so it
 * reflects exactly the leftover space Chromium's own pagination produces
 * there — it stops mid-page and starts fresh, the same thing this loop is
 * already modelling.
 */
export interface PageBreakGap {
    startIdx: number;
    /**
     * `(pageTop + pageHeightPx) - currentPageContentEnd` at the break,
     * clamped to `>= 0`. A prior EXTEND can leave `currentPageContentEnd`
     * PAST the nominal page bottom (that's the whole point of EXTEND — the
     * group is taller than one page) — there's no leftover space to report
     * in that case, so the clamp floors it at zero rather than going negative.
     */
    unusedPx: number;
}

/**
 * A single-row table row GROUP taller than this fraction of the page is
 * decomposed into per-cell-paragraph SEGMENT units (`buildAtomicUnits`,
 * pageStartUnits.ts — RECUR-27604 §14 in-row split) so the model can plan a
 * cut INSIDE it instead of PUSHing it whole (orphaning a near-blank page
 * before it) or EXTENDing it (planning a page taller than the physical
 * paper, which desyncs the exported PDF from the editor). Rows at or below
 * the ratio keep their atomic behavior. Declared here (not in
 * `pageStartUnits.ts`) for the same "can't drift" reason
 * `LINE_ORPHAN_WIDOW_MIN_LINES` is.
 */
export const OVERSIZED_ROW_SPLIT_RATIO = 0.7;

/**
 * Runs the fit-loop over pre-built groups and returns, for every page-start
 * AFTER the first, the flat-unit INDEX (Delta v3 — see `pageBreakModel`'s own
 * doc comment) paired with the space left unused on the page above it
 * (RECUR-27304 gap-display). `pageBreakModel` is a thin wrapper over this —
 * the fit-loop itself lives ONLY here, so the two can never disagree on
 * *which* unit starts a page, only on whether the caller also wants the gap.
 *
 * EXTEND never appears here (unchanged from `pageBreakModel`): the group is
 * kept together on the SAME page instead of being fragmented, so there is no
 * boundary — and therefore no whitespace — to report for it. Chromium
 * fragments an EXTENDed group across pages with no single fixed cut, unlike
 * a PUSH/forced break's hard boundary, so there's genuinely nothing to show.
 *
 * @param groups group boundaries built by `buildPaginationGroups(blocks)`.
 * @param blocks per-block geometry, indexed the same as the `blocks` array `groups` was built from.
 * @param pageHeightPx the SERVER page's usable content height (`getServerPageHeightPx`).
 */
export function pageBreakModelWithGaps(groups: PaginationGroup[], blocks: BlockGeometry[], pageHeightPx: number): PageBreakGap[] {
    if (groups.length === 0 || !Number.isFinite(pageHeightPx) || pageHeightPx <= 0) return [];

    const result: PageBreakGap[] = [];
    let pageTop = 0;
    let currentPageContentEnd = 0;
    let hasBlockOnCurrentPage = false;

    for (const group of groups) {
        const first = blocks[group.startIdx];
        const last = blocks[group.endIdx];
        if (!first || !last || first.offsetHeight <= 0) continue; // zero-height (hidden/collapsed) — skip, mirrors planPages

        const naturalTop = first.offsetTop;
        const naturalEnd = last.offsetTop + last.offsetHeight;
        const groupHeight = naturalEnd - naturalTop;

        // The current page's real bottom: its nominal height, or — once a
        // prior EXTEND has grown it — the actual accumulated content end,
        // whichever is greater (mirrors planPages' pageNaturalBottom idea,
        // minus its raster-only bottom-padding term).
        const pageBottom = Math.max(pageTop + pageHeightPx, currentPageContentEnd);

        if (!hasBlockOnCurrentPage) {
            // The document's FIRST block: it lands on page 1 unconditionally,
            // AND page 1 is anchored to this block's OWN top — not absolute
            // 0 — exactly how every later page starts at its first group's
            // `naturalTop` after a PUSH (below). This matters whenever real
            // layout sits ABOVE the first content block that is NOT page
            // content and so must not eat into page 1's fill budget: the
            // editor's on-page-1 logo band inset (~73px, RECUR-27604) pushes
            // `first.offsetTop` down by that inset, and pinning page 1 to 0
            // otherwise loses that many px of capacity, breaking page 1 up to
            // two rows EARLY versus the server PDF. The export clone has no
            // such inset (the band lives in the per-page wrapper `<thead>`,
            // reserved via capacity) and every non-logo document's first
            // block sits at 0, so `naturalTop === 0` there and this is a
            // no-op — server-PDF and non-logo pagination stay byte-identical
            // while the editor preview now matches the PDF.
            pageTop = naturalTop;
            currentPageContentEnd = naturalEnd;
            hasBlockOnCurrentPage = true;
            continue;
        }

        if (naturalEnd <= pageBottom) {
            // Fits on the current page.
            currentPageContentEnd = naturalEnd;
            hasBlockOnCurrentPage = true;
            continue;
        }

        if (groupHeight > pageHeightPx) {
            // EXTEND — taller than any single page on its own. Kept together
            // on the current page rather than fragmented; no new boundary.
            currentPageContentEnd = naturalEnd;
            hasBlockOnCurrentPage = true;
            continue;
        }

        // PUSH — a group that doesn't fit the space left on this page but
        // would fit a fresh one: start a new page here. Push the UNIT INDEX
        // (Delta v3), not naturalTop — callers project it. `unusedPx` is
        // measured BEFORE pageTop/currentPageContentEnd roll over below — it
        // belongs to the page being LEFT BEHIND, not the new one.
        result.push({ startIdx: group.startIdx, unusedPx: Math.max(0, pageTop + pageHeightPx - currentPageContentEnd) });
        pageTop = naturalTop;
        currentPageContentEnd = naturalEnd;
        hasBlockOnCurrentPage = true;
    }

    return result;
}

/**
 * Runs the fit-loop over pre-built groups and returns the flat-unit INDEX
 * (into `blocks`/`groups`, NOT its `offsetTop` — Delta v3) of the first unit
 * of every page AFTER the first — the first page's start is never itself a
 * boundary (mirrors `PageBoundaryOverlay`'s existing "Page 2, Page 3, …"
 * numbering: no chip for page 1). Callers project the index onward: the
 * overlay reads `blocks[index].offsetTop`, the exporter reads the LIVE
 * element that index maps to (see `pageStartUnits.ts`).
 *
 * Thin wrapper over `pageBreakModelWithGaps` (RECUR-27304 gap-display) —
 * kept as its own function/return-shape so existing callers that only need
 * the index list are byte-for-byte unaffected by the gap addition.
 *
 * @param groups group boundaries built by `buildPaginationGroups(blocks)`.
 * @param blocks per-block geometry, indexed the same as the `blocks` array `groups` was built from.
 * @param pageHeightPx the SERVER page's usable content height (`getServerPageHeightPx`).
 */
export function pageBreakModel(groups: PaginationGroup[], blocks: BlockGeometry[], pageHeightPx: number): number[] {
    return pageBreakModelWithGaps(groups, blocks, pageHeightPx).map((gap) => gap.startIdx);
}

/**
 * Re-indexes `buildPaginationGroups`' BLOCK-level groups (exportToPdf.ts:475)
 * onto a FLAT atomic-unit list where a table block has been decomposed into
 * one unit per `<tr>` (RECUR-27304 Delta v3 — see usePageBoundaries.ts's
 * row-geometry building, which constructs `rowCounts`/`unitStart` and calls
 * this before `pageBreakModel`). Tables split BETWEEN rows now, matching
 * Chromium's native `break-inside:auto` fragmentation on `tr` (RECUR-26249),
 * instead of moving a whole heading+table group to the next page as one
 * indivisible block.
 *
 * Re-indexing only needs to special-case a group's `endIdx`, because
 * `buildPaginationGroups`' GROUP_END_TAGS makes a table ALWAYS terminate
 * whichever group it belongs to — its own standalone `{i, i}` group, or a
 * preceding sticky heading's lookahead — so a table block is never a group's
 * `startIdx` with more content swept in after it.
 *
 *   • `rowCounts[endIdx] < 2` (no table there, or a table we found fewer than
 *     2 rows for — can't usefully split) — the group is re-indexed 1:1, byte-
 *     identical semantics to today.
 *   • `rowCounts[endIdx] >= 2` — the ORIGINAL group (sticky heading, if any,
 *     through the table) is re-indexed to cover just the table's FIRST row —
 *     `buildPaginationGroups`' "heading stays with its table" intent, applied
 *     at row granularity: a heading can never be orphaned alone at a page
 *     bottom, because it and row 0 are one atomic unit. Every remaining row
 *     becomes its OWN standalone one-unit group, so the fit-loop above can
 *     seam BETWEEN any two rows instead of only before the whole table.
 *
 * @param rowCounts parallel to the ORIGINAL blocks array `groups` was built
 * from — the number of `<tr>` rows detected for that block (0 for a
 * non-table, or a table with none found).
 * @param unitStart parallel to the ORIGINAL blocks array — the flat-array
 * index of that block's FIRST unit (its only unit, when not decomposed).
 */
export function reindexGroupsForRows(groups: PaginationGroup[], rowCounts: number[], unitStart: number[]): PaginationGroup[] {
    const result: PaginationGroup[] = [];
    for (const g of groups) {
        const rows = rowCounts[g.endIdx] ?? 0;
        const start = unitStart[g.startIdx];
        if (rows < 2) {
            // Not decomposed — the block contributed exactly one unit.
            result.push({ startIdx: start, endIdx: unitStart[g.endIdx] });
            continue;
        }
        const tableFirstRowIdx = unitStart[g.endIdx];
        result.push({ startIdx: start, endIdx: tableFirstRowIdx }); // sticky heading (if any) + row 0
        for (let r = 1; r < rows; r++) {
            const rowIdx = tableFirstRowIdx + r;
            result.push({ startIdx: rowIdx, endIdx: rowIdx }); // every other row breaks independently
        }
    }
    return result;
}

/**
 * A decomposed paragraph needs at least this many LINE units for both the
 * orphan pair (its own first 2 lines) and the widow pair (its own last 2
 * lines) to exist without overlapping (Word-default 2/2 orphan/widow
 * control — RECUR-27304 Delta v5). `buildAtomicUnits` (pageStartUnits.ts)
 * only decomposes a paragraph when it measured at least this many lines —
 * this constant is re-exported here (not re-declared) so the two can never
 * drift apart.
 */
export const LINE_ORPHAN_WIDOW_MIN_LINES = 4;

/**
 * Generalizes `reindexGroupsForRows` (RECUR-27304 Delta v5) to ALSO handle a
 * long TEXT PARAGRAPH decomposed into one flat unit per rendered LINE
 * (`buildAtomicUnits`' line-decomposition branch, mirroring its existing
 * table-row branch) — needed as ONE combined pass, not two chained reindex
 * calls, because `reindexGroupsForRows`' own "not decomposed" 1:1 fallback
 * assumes `unitStart[endIdx]` names the group's WHOLE last block; that
 * assumption only holds when NOTHING else decomposed that index. A
 * line-decomposed paragraph breaks it (its `unitStart` is only its FIRST
 * line), so both decompositions must be considered in the SAME pass. A
 * group's `endIdx` block can only ever be a table XOR a decomposed
 * paragraph XOR neither — `rowCounts[endIdx]` and `lineCounts[endIdx]` are
 * therefore mutually exclusive at every index (a block's own tagName can't
 * be both a `<table>`/`.tableScroll` wrapper and a plain `<p>` — see
 * `isLineDecomposableBlock`).
 *
 * Line orphan/widow control (2/2, Word default): a decomposed paragraph's
 * group re-maps to
 *   • [group start .. line 1] — sticky heading (if any) + the paragraph's
 *     FIRST TWO lines, one atomic group (orphan control: a page can never
 *     start mid-paragraph at its very first line alone — that seam instead
 *     lands on the group's own `start`, i.e. the heading, or line 0 itself
 *     when there's no heading, both of which are 'block'-kind: "the whole
 *     paragraph moves", nothing to split there).
 *   • every line from 2 up to (but excluding) the last two — each its OWN
 *     standalone one-unit group, independently breakable.
 *   • [second-to-last line .. last line] — one atomic group (widow control:
 *     a page can never end with only the paragraph's last line stranded,
 *     and by construction line 1 can never be a group's OWN `startIdx`
 *     either — it only ever appears as the first group's `endIdx`).
 * `lines - 2` (the middle range's exclusive end) is never less than 2 (the
 * widow group's own start) because `buildAtomicUnits` never decomposes
 * below `LINE_ORPHAN_WIDOW_MIN_LINES`.
 *
 * Table rows are handled BYTE-IDENTICAL to `reindexGroupsForRows` (that
 * function is kept, unmodified and still independently tested, as the
 * narrower row-only building block this one's table branch mirrors).
 *
 * RECUR-27304 review fix: `buildPaginationGroups` can sweep a sticky heading
 * together with SEVERAL following blocks (its own lookahead walks up to
 * `MAX_STICKY_LOOKBACK` blocks, stopping only at the next heading, a forced
 * break, or a `GROUP_END_TAG` — a plain paragraph never stops it). Only the
 * group's OWN `endIdx` used to be checked for decomposition here, so an
 * earlier line-decomposable paragraph INSIDE the same group (a table can
 * only ever be the group's `endIdx` — `GROUP_END_TAGS` always terminates a
 * group at it, so it needs no equivalent walk) had its line units silently
 * swallowed into one giant glued span, unreachable by the fit-loop as a
 * break point — re-creating the exact big-push-gap bug this decomposition
 * exists to fix, just for heading-led MULTI-paragraph sections. Fixed by
 * walking every block in the group instead of only its `endIdx`: a run of
 * not-yet-decomposed blocks (the sticky heading, if any, plus any
 * non-decomposable paragraphs) stays glued forward onto the NEXT
 * decomposable block's own orphan pair — generalizing the endIdx-only
 * special case below to fire at whichever block it's actually needed for —
 * and every decomposable block gets its own independent 2/2 orphan/widow
 * line grouping. A trailing run of non-decomposable blocks AFTER the last
 * decomposition point (before the group's own end) becomes its own glued
 * group, same as a plain standalone block would be.
 *
 * @param rowCounts parallel to the ORIGINAL blocks array — see `reindexGroupsForRows`.
 * @param lineCounts parallel to the ORIGINAL blocks array — the number of LINE
 * units `buildAtomicUnits` decomposed that block into (0 when not a decomposed
 * paragraph — wrong block type, too short, or line measurement failed).
 * @param unitStart parallel to the ORIGINAL blocks array — see `reindexGroupsForRows`.
 */
export function reindexGroupsForRowsAndLines(
    groups: PaginationGroup[],
    rowCounts: number[],
    lineCounts: number[],
    unitStart: number[],
): PaginationGroup[] {
    const result: PaginationGroup[] = [];
    for (const g of groups) {
        // Flat-unit index where the current "glued" run of not-yet-attached
        // content begins — starts at the group's own leading edge (the
        // sticky heading, if any) and is re-anchored to just past a
        // decomposed block's last unit every time one is found below, so a
        // LATER decomposable block in the same group glues only to whatever
        // (possibly nothing) immediately precedes IT, not all the way back
        // to the group's start.
        let runStart = unitStart[g.startIdx];

        for (let blockIdx = g.startIdx; blockIdx <= g.endIdx; blockIdx++) {
            const rows = rowCounts[blockIdx] ?? 0;
            if (rows >= 2) {
                // A table always terminates its OWN group (buildPaginationGroups'
                // GROUP_END_TAGS) — this can only ever be the LAST iteration,
                // byte-identical to the original endIdx-only special case.
                const tableFirstRowIdx = unitStart[blockIdx];
                result.push({ startIdx: runStart, endIdx: tableFirstRowIdx }); // glued run (if any) + row 0
                for (let r = 1; r < rows; r++) {
                    const rowIdx = tableFirstRowIdx + r;
                    result.push({ startIdx: rowIdx, endIdx: rowIdx }); // every other row breaks independently
                }
                runStart = tableFirstRowIdx + rows;
                continue;
            }

            const lines = lineCounts[blockIdx] ?? 0;
            if (lines >= LINE_ORPHAN_WIDOW_MIN_LINES) {
                const firstLineIdx = unitStart[blockIdx];
                result.push({ startIdx: runStart, endIdx: firstLineIdx + 1 }); // glued run (if any) + THIS block's own orphan pair (lines 0-1)
                for (let line = 2; line < lines - 2; line++) {
                    const lineIdx = firstLineIdx + line;
                    result.push({ startIdx: lineIdx, endIdx: lineIdx }); // breakable middle line
                }
                const widowEnd = firstLineIdx + lines - 1;
                result.push({ startIdx: firstLineIdx + lines - 2, endIdx: widowEnd }); // last two lines (widow pair)
                runStart = widowEnd + 1;
                continue;
            }

            // Not decomposable — folds into the current glued run; only
            // flushed once the loop hits the next decomposition point, or
            // (below) the group's own end.
        }

        // Either nothing in the group decomposed (byte-identical 1:1
        // fallback), or a trailing run of non-decomposed blocks follows the
        // LAST decomposition point — both close out as one glued group
        // through the group's own end. Skipped when the group's own last
        // block WAS the most recent decomposition (runStart already moved
        // past unitStart[g.endIdx]) — nothing left to flush.
        if (runStart <= unitStart[g.endIdx]) {
            result.push({ startIdx: runStart, endIdx: unitStart[g.endIdx] });
        }
    }
    return result;
}
